import React, { useState } from 'react';
import { Container, Row, Col, Card, Table, Badge, ProgressBar, Alert, Button, Form, Spinner, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Activity, ExclamationTriangle, CheckCircle, XCircle, ArrowClockwise, GraphUp, GraphDown, Clock, ChevronDown, ChevronRight, InfoCircle } from 'react-bootstrap-icons';
import { useAccount } from '../contexts/AccountContext';
import { useGetUserPreferencesQuery, useGetUnifiedEmailMonitoringQuery } from '../services/api';
import EMMSectionHeader from './EMMSectionHeader';
import {
  getTenantTimezoneFromPreferencesData,
  toLocaleStringInTimezone,
} from '../utils/tenantTimezone';
import { useUserPreferencesQueryArg } from '../hooks/useTenantIdForPreferences';

const AUTO_REFRESH_INTERVAL = 60000;

// Sticky preference for the Overall|Campaign Health toggle. Stored in
// localStorage so a deep-link refresh keeps the operator's chosen view.
// See backend src/lib/classify-send-kind.ts +
// docs/md/CAMPAIGN_VS_OVERALL_HEALTH.md for what these modes mean.
const VIEW_MODE_STORAGE_KEY = 'kg.identityHealth.viewMode';
const VALID_VIEW_MODES = ['overall', 'campaign'];
const readStoredViewMode = () => {
  try {
    const v = window.localStorage.getItem(VIEW_MODE_STORAGE_KEY);
    return VALID_VIEW_MODES.includes(v) ? v : 'overall';
  } catch (_e) {
    return 'overall';
  }
};

// Severity-proportional pill styling. Critical stays loud (solid red) because
// it's actually critical; warning and info are intentionally quieter so that a
// single in-range hard bounce on an otherwise healthy domain doesn't render as
// a screaming red block. Outline-style pills keep the row scannable.
const getAlertPillStyle = (severity) => {
  switch (severity) {
    case 'critical':
      return {
        backgroundColor: '#f8d7da',
        borderColor: '#f5c2c7',
        color: '#842029',
      };
    case 'warning':
      return {
        backgroundColor: '#fff8e6',
        borderColor: '#f3d57a',
        color: '#7a5b00',
      };
    case 'info':
    default:
      return {
        backgroundColor: '#f3f4f6',
        borderColor: '#d6d8db',
        color: '#495057',
      };
  }
};

const getAlertIcon = (severity) => {
  switch (severity) {
    case 'critical':
      return '⛔';
    case 'warning':
      return '⚠';
    case 'info':
    default:
      return 'ℹ';
  }
};

const IdentityHealthMonitoring = () => {
  const { selectedAccount, isAllAccountsSelected, isLoading } = useAccount();
  const userPreferencesQueryArg = useUserPreferencesQueryArg();
  const { data: userPreferencesData } = useGetUserPreferencesQuery(userPreferencesQueryArg);
  const tenantTimezone = getTenantTimezoneFromPreferencesData(userPreferencesData);
  const [selectedTimeRange, setSelectedTimeRange] = useState('24h');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [viewMode, setViewMode] = useState(readStoredViewMode);
  const handleViewModeChange = (next) => {
    if (!VALID_VIEW_MODES.includes(next) || next === viewMode) return;
    setViewMode(next);
    try {
      window.localStorage.setItem(VIEW_MODE_STORAGE_KEY, next);
    } catch (_e) { /* localStorage unavailable; fine to no-op */ }
  };
  // Per-row "Why?" expander state — keyed by identity.id. Closed by default
  // so the table stays scannable; click opens a full reason list inline.
  const [expandedReasons, setExpandedReasons] = useState({});

  const toggleReasons = (identityId) => {
    setExpandedReasons((prev) => ({ ...prev, [identityId]: !prev[identityId] }));
  };

  const queryArgs = {
    timeRange: selectedTimeRange,
    mode: viewMode,
    ...(isAllAccountsSelected ? {} : { accountId: selectedAccount?.id }),
  };

  const {
    data: healthData,
    isLoading: loading,
    isFetching,
    error,
    refetch,
  } = useGetUnifiedEmailMonitoringQuery(queryArgs, {
    pollingInterval: autoRefresh ? AUTO_REFRESH_INTERVAL : 0,
  });

  const identities = healthData?.data?.identities || [];
  const summary = healthData?.data?.summary || { healthy: 0, warning: 0, critical: 0, totalVolume: 0 };
  // Server echoes the mode it actually answered with — prefer that over our
  // local state when rendering disclaimers, so a stale cached response can't
  // make the banner lie about which view the numbers came from.
  const respondedMode = healthData?.data?.mode || viewMode;
  const taggingStartIso = healthData?.data?.taggingStartIso || null;
  // Aggregate excluded counts across all visible identities so the
  // Campaign-mode banner can honestly report total backfill coverage in
  // the current window without forcing the user to scan every row.
  const exclusionTotals = identities.reduce(
    (acc, i) => {
      acc.excludedSends += Number(i?.metrics?.excludedSendCount || 0);
      acc.excludedBounces += Number(i?.metrics?.excludedBounceCount || 0);
      acc.untaggedSends += Number(i?.metrics?.untaggedSendCount || 0);
      acc.untaggedBounces += Number(i?.metrics?.untaggedBounceCount || 0);
      return acc;
    },
    { excludedSends: 0, excludedBounces: 0, untaggedSends: 0, untaggedBounces: 0 }
  );
  const formatTaggingStart = (iso) => {
    if (!iso) return null;
    try {
      return toLocaleStringInTimezone(new Date(iso), 'en-US', { dateStyle: 'medium' }, tenantTimezone);
    } catch (_e) {
      return iso;
    }
  };

  const getHealthStatusBadge = (status) => {
    const style = { fontSize: '0.7rem', padding: '0.2rem 0.45rem' };
    switch (status) {
      case 'healthy':
        return <Badge bg="success" style={style}><CheckCircle size={10} className="me-1" />Healthy</Badge>;
      case 'warning':
        return <Badge bg="warning" style={style}><ExclamationTriangle size={10} className="me-1" />Warning</Badge>;
      case 'critical':
        return <Badge bg="danger" style={style}><XCircle size={10} className="me-1" />Critical</Badge>;
      default:
        return <Badge bg="secondary" style={style}>Unknown</Badge>;
    }
  };

  const getHealthScoreColor = (score) => {
    if (score >= 90) return 'success';
    if (score >= 70) return 'warning';
    return 'danger';
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up':
        return <GraphUp size={10} className="text-success" />;
      case 'down':
        return <GraphDown size={10} className="text-danger" />;
      default:
        return <Clock size={10} className="text-muted" />;
    }
  };

  const handleRefresh = () => {
    refetch();
  };

  if (loading && !healthData) {
    return (
      <Container className="py-4" style={{ position: 'relative', minHeight: '400px' }}>
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            borderRadius: '8px'
          }}
        >
          <div className="text-center">
            <ArrowClockwise size={24} className="text-primary mb-2" style={{ animation: 'spin 1s linear infinite' }} />
            <div className="text-muted" style={{ fontSize: '0.85rem' }}>Loading health data...</div>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <>
      <EMMSectionHeader title="Identity Health Monitoring" showHelp />
      <Container className="py-3" style={{ position: 'relative' }}>
      {/* Action bar */}
      <div className="d-flex align-items-center gap-2 mb-3">
        <Button
          variant="outline-secondary"
          size="sm"
          className="d-flex align-items-center"
          style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
          onClick={handleRefresh}
          disabled={isFetching}
          title="Refresh health data"
        >
          <ArrowClockwise
            size={12}
            className="me-1"
            style={isFetching ? { animation: 'spin 1s linear infinite' } : undefined}
          />
          {isFetching ? 'Refreshing...' : 'Refresh'}
        </Button>
        <Form.Check
          type="switch"
          id="auto-refresh-identity-health"
          label="Auto Refresh"
          checked={autoRefresh}
          onChange={(e) => setAutoRefresh(e.target.checked)}
          className="mb-0"
          style={{ fontSize: '0.8rem' }}
        />
      </div>
      {/* Loading Overlay */}
      {(isLoading || (isFetching && !loading)) && (
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            borderRadius: '8px'
          }}
        >
          <div className="text-center">
            <ArrowClockwise size={24} className="text-primary mb-2" style={{ animation: 'spin 1s linear infinite' }} />
            <div className="text-muted" style={{ fontSize: '0.85rem' }}>{isLoading ? `Switching to ${selectedAccount.alias}...` : 'Refreshing health data...'}</div>
          </div>
        </div>
      )}
      <Row className="mb-3 align-items-center">
        <Col md="auto">
          <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>Monitor health and performance of all sending identities</p>
        </Col>
        <Col className="d-flex justify-content-end">
          {/*
            Overall vs Campaign-Only health view toggle. Overall is the
            default ISP-reputation view (warmup + campaign + transactional
            all count). Campaign filters down to emailKind === 'campaign'
            on BOTH sides of the rate so list-quality is visible without
            warmup activity masking it. See backend
            docs/md/CAMPAIGN_VS_OVERALL_HEALTH.md for full rationale.
          */}
          <div
            role="group"
            aria-label="Health view mode"
            className="btn-group"
            style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}
          >
            <OverlayTrigger
              placement="top"
              overlay={(
                <Tooltip>
                  Includes warmup, transactional, and campaign sends — the true
                  reputation signal ISPs see.
                </Tooltip>
              )}
            >
              <Button
                size="sm"
                variant={viewMode === 'overall' ? 'primary' : 'outline-secondary'}
                onClick={() => handleViewModeChange('overall')}
                aria-pressed={viewMode === 'overall'}
                style={{ fontSize: '0.75rem', padding: '0.25rem 0.65rem' }}
              >
                Overall Health
              </Button>
            </OverlayTrigger>
            <OverlayTrigger
              placement="top"
              overlay={(
                <Tooltip>
                  Campaign sends only — excludes warmup and transactional
                  on both numerator and denominator. Use to diagnose list
                  quality.
                </Tooltip>
              )}
            >
              <Button
                size="sm"
                variant={viewMode === 'campaign' ? 'primary' : 'outline-secondary'}
                onClick={() => handleViewModeChange('campaign')}
                aria-pressed={viewMode === 'campaign'}
                style={{ fontSize: '0.75rem', padding: '0.25rem 0.65rem' }}
              >
                Campaign Only
              </Button>
            </OverlayTrigger>
          </div>
        </Col>
      </Row>

      {/*
        Campaign-mode disclosure banner. Renders only when the server
        confirms it answered in 'campaign' mode AND there's actually
        excluded volume to disclose, so the Overall view stays clean and
        Campaign domains with no warmup don't see noise either.
      */}
      {respondedMode === 'campaign'
        && (exclusionTotals.excludedSends > 0 || exclusionTotals.excludedBounces > 0) && (
        <Alert
          variant="info"
          className="mb-3 py-2 px-3"
          style={{ fontSize: '0.8rem' }}
        >
          <div className="d-flex align-items-start">
            <InfoCircle size={14} className="me-2 mt-1 flex-shrink-0" />
            <div>
              <strong>Campaign-Only view:</strong>{' '}
              {exclusionTotals.excludedSends.toLocaleString()} send{exclusionTotals.excludedSends === 1 ? '' : 's'}{' '}
              and {exclusionTotals.excludedBounces.toLocaleString()} bounce{exclusionTotals.excludedBounces === 1 ? '' : 's'}{' '}
              in this window are excluded (warmup, transactional, or untagged legacy rows).
              {exclusionTotals.untaggedSends > 0 && taggingStartIso && (
                <>
                  {' '}
                  Of those, {exclusionTotals.untaggedSends.toLocaleString()} send{exclusionTotals.untaggedSends === 1 ? '' : 's'}
                  {' '}pre-date message-kind tagging — Campaign-Only data is fully complete from{' '}
                  <strong>{formatTaggingStart(taggingStartIso)}</strong> onward.
                </>
              )}
              {' '}Switch to <strong>Overall Health</strong> for the true reputation view.
            </div>
          </div>
        </Alert>
      )}

      {/* Error Banner */}
      {error && (
        <Alert variant="danger" className="mb-3" style={{ fontSize: '0.82rem' }} dismissible>
          <strong>Error loading health data:</strong>{' '}
          {error?.data?.message || error?.error || 'Unable to connect to health monitoring API'}
        </Alert>
      )}

      {/* Summary Cards */}
      <Row className="mb-3 g-2">
        <Col md={3}>
          <Card className="text-center" style={{ fontSize: '0.85rem' }}>
            <Card.Body className="py-2 px-3">
              <div className="text-success fw-bold" style={{ fontSize: '0.8rem' }}>Healthy</div>
              <div className="fw-bold" style={{ fontSize: '1.4rem', lineHeight: 1.2 }}>{summary.healthy}</div>
              <div className="text-muted" style={{ fontSize: '0.7rem' }}>Identities</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center" style={{ fontSize: '0.85rem' }}>
            <Card.Body className="py-2 px-3">
              <div className="text-warning fw-bold" style={{ fontSize: '0.8rem' }}>Warning</div>
              <div className="fw-bold" style={{ fontSize: '1.4rem', lineHeight: 1.2 }}>{summary.warning}</div>
              <div className="text-muted" style={{ fontSize: '0.7rem' }}>Identities</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center" style={{ fontSize: '0.85rem' }}>
            <Card.Body className="py-2 px-3">
              <div className="text-danger fw-bold" style={{ fontSize: '0.8rem' }}>Critical</div>
              <div className="fw-bold" style={{ fontSize: '1.4rem', lineHeight: 1.2 }}>{summary.critical}</div>
              <div className="text-muted" style={{ fontSize: '0.7rem' }}>Identities</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center" style={{ fontSize: '0.85rem' }}>
            <Card.Body className="py-2 px-3">
              <div className="text-info fw-bold" style={{ fontSize: '0.8rem' }}>
                {respondedMode === 'campaign' ? 'Campaign Volume' : 'Total Volume'}
              </div>
              <div className="fw-bold" style={{ fontSize: '1.4rem', lineHeight: 1.2 }}>{summary.totalVolume.toLocaleString()}</div>
              <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                {respondedMode === 'campaign' ? 'Campaign sends only' : 'Emails sent (all kinds)'}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Time Range Selector */}
      <Row className="mb-3">
        <Col>
          <Card>
            <Card.Body className="py-2 px-3">
              <div className="d-flex justify-content-between align-items-center">
                <span className="fw-bold" style={{ fontSize: '0.85rem' }}>Monitoring Period</span>
                <Form.Select
                  size="sm"
                  style={{ width: '180px', fontSize: '0.82rem' }}
                  value={selectedTimeRange}
                  onChange={(e) => setSelectedTimeRange(e.target.value)}
                >
                  <option value="1h">Last Hour</option>
                  <option value="24h">Last 24 Hours</option>
                  <option value="7d">Last 7 Days</option>
                  <option value="30d">Last 30 Days</option>
                </Form.Select>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Identities Health Table */}
      <Row>
        <Col>
          <Card>
            <Card.Header className="py-2">
              <span className="fw-bold" style={{ fontSize: '0.85rem' }}>Identity Health Status</span>
            </Card.Header>
            <Card.Body className="p-0">
              <Table responsive hover className="mb-0" size="sm">
                <thead className="table-light">
                  <tr>
                    <th style={{ fontSize: '0.82rem' }}>Identity</th>
                    <th style={{ fontSize: '0.82rem' }}>Health Score</th>
                    <th style={{ fontSize: '0.82rem' }}>Status</th>
                    <th style={{ fontSize: '0.82rem' }}>Deliverability</th>
                    <th style={{ fontSize: '0.82rem' }}>Bounce Rate</th>
                    <th style={{ fontSize: '0.82rem' }}>Complaint Rate</th>
                    <th style={{ fontSize: '0.82rem' }}>Reputation</th>
                    <th style={{ fontSize: '0.82rem' }}>Volume</th>
                    <th style={{ fontSize: '0.82rem' }}>Alerts</th>
                    <th style={{ fontSize: '0.82rem' }}>Last Checked</th>
                  </tr>
                </thead>
                <tbody>
                  {identities.length === 0 && (
                    <tr>
                      <td colSpan={10} className="text-center py-4 text-muted" style={{ fontSize: '0.82rem' }}>
                        {error ? 'Unable to load identity data' : 'No domains configured. Add domains in the Domains page to start monitoring.'}
                      </td>
                    </tr>
                  )}
                  {identities.map((identity) => (
                    <tr key={identity.id}>
                      <td style={{ fontSize: '0.85rem' }}>
                        <div>
                          <strong>{identity.name}</strong>
                          {identity.details?.emailCount > 0 && (
                            <div className="text-muted" style={{ fontSize: '0.7rem' }}>{identity.details.emailCount} email account{identity.details.emailCount !== 1 ? 's' : ''}</div>
                          )}
                        </div>
                      </td>
                      <td style={{ fontSize: '0.85rem' }}>
                        <div className="d-flex align-items-center">
                          <ProgressBar
                            now={identity.healthScore}
                            variant={getHealthScoreColor(identity.healthScore)}
                            style={{ width: '50px', height: '6px' }}
                            className="me-2"
                          />
                          <span className="fw-bold">{identity.healthScore}%</span>
                        </div>
                      </td>
                      <td>{getHealthStatusBadge(identity.status)}</td>
                      <td style={{ fontSize: '0.85rem' }}>
                        <div className="d-flex align-items-center gap-1">
                          <span>{identity.metrics.deliverability}%</span>
                          {getTrendIcon(identity.trends.deliverability)}
                        </div>
                      </td>
                      <td style={{ fontSize: '0.85rem' }}>
                        {(() => {
                          const m = identity.metrics || {};
                          // Backend now returns bounceRateLabel (e.g. "2.1% (3/145)" or "n/a"
                          // when sentCount === 0). Fall back to a numeric % for older payloads.
                          const label = typeof m.bounceRateLabel === 'string' && m.bounceRateLabel.length > 0
                            ? m.bounceRateLabel
                            : (m.bounceRate === null || m.bounceRate === undefined ? 'n/a' : `${m.bounceRate}%`);
                          const hard = Number(m.hardBounceCount || 0);
                          const soft = Number(m.softBounceCount || 0);
                          const unknown = Number(m.unknownBounceCount || 0);
                          const tooltipBody = (
                            <Tooltip id={`bounce-${identity.id}`}>
                              <div style={{ textAlign: 'left' }}>
                                <div><strong>Hard:</strong> {hard}</div>
                                <div><strong>Soft:</strong> {soft}</div>
                                <div><strong>Unknown type:</strong> {unknown}</div>
                                {m.bounceVolumeTruncated && (
                                  <div className="mt-1"><em>Counts capped at page limit; lower bound only.</em></div>
                                )}
                                <div className="mt-1" style={{ fontSize: '0.75rem' }}>
                                  Source: bounce notifications received back at Kross Global from any relay.
                                </div>
                              </div>
                            </Tooltip>
                          );
                          return (
                            <OverlayTrigger placement="top" overlay={tooltipBody}>
                              <div className="d-flex align-items-center gap-1" style={{ cursor: 'help' }}>
                                <span className={hard > 0 ? 'text-danger fw-bold' : soft > 0 ? 'text-warning' : ''}>{label}</span>
                                {getTrendIcon(identity.trends.bounceRate)}
                              </div>
                            </OverlayTrigger>
                          );
                        })()}
                      </td>
                      <td style={{ fontSize: '0.85rem' }}>
                        {(() => {
                          const m = identity.metrics || {};
                          // complaintRate is now the literal string 'n/a' (FBL not wired).
                          // Surface honestly with a tooltip that explains why.
                          const isNa = m.complaintRate === 'n/a' || m.complaintRate === null || m.complaintRate === undefined;
                          if (isNa) {
                            return (
                              <OverlayTrigger
                                placement="top"
                                overlay={
                                  <Tooltip id={`complaint-${identity.id}`}>
                                    {m.complaintRateNote || 'ISP feedback-loop reports go to the relay, not Kross Global. We can\'t derive a per-domain complaint rate from inbound mail alone.'}
                                  </Tooltip>
                                }
                              >
                                <div className="d-flex align-items-center gap-1 text-muted" style={{ cursor: 'help' }}>
                                  <span>n/a</span>
                                  <InfoCircle size={10} />
                                </div>
                              </OverlayTrigger>
                            );
                          }
                          return (
                            <div className="d-flex align-items-center gap-1">
                              <span>{m.complaintRate}%</span>
                              {getTrendIcon(identity.trends.complaintRate)}
                            </div>
                          );
                        })()}
                      </td>
                      <td style={{ fontSize: '0.85rem' }}>
                        <div className="d-flex align-items-center gap-1">
                          <span>{identity.metrics.reputation}</span>
                          {getTrendIcon(identity.trends.reputation)}
                        </div>
                      </td>
                      <td style={{ fontSize: '0.85rem' }}>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip id={`volume-${identity.id}`}>
                              Outbound emails sent from this domain in the selected window
                              (across every relay), counted from the unified emails table.
                            </Tooltip>
                          }
                        >
                          <span className="fw-bold" style={{ cursor: 'help' }}>
                            {(identity.metrics?.volume || 0).toLocaleString()}
                          </span>
                        </OverlayTrigger>
                      </td>
                      <td>
                        {(() => {
                          const alerts = Array.isArray(identity.alerts) ? identity.alerts : [];
                          if (alerts.length === 0) {
                            return <span className="text-muted" style={{ fontSize: '0.75rem' }}>—</span>;
                          }
                          // Show the highest-severity alert message inline so the user sees
                          // the actual reason (no more guessing 'critical' vs 'warning').
                          // Sort: critical > warning > info, then by index.
                          const severityRank = { critical: 0, warning: 1, info: 2 };
                          const sorted = [...alerts].sort((a, b) =>
                            (severityRank[a.type] ?? 99) - (severityRank[b.type] ?? 99)
                          );
                          const top = sorted[0];
                          const expanded = Boolean(expandedReasons[identity.id]);
                          const topStyle = getAlertPillStyle(top.type);
                          return (
                            <div className="d-flex flex-column" style={{ maxWidth: 360 }}>
                              <span
                                className="mb-1 align-self-start d-inline-flex align-items-start"
                                style={{
                                  ...topStyle,
                                  fontSize: '0.72rem',
                                  padding: '0.25rem 0.5rem',
                                  borderRadius: '0.35rem',
                                  borderStyle: 'solid',
                                  borderWidth: '1px',
                                  whiteSpace: 'normal',
                                  textAlign: 'left',
                                  lineHeight: 1.35,
                                  fontWeight: 500,
                                }}
                              >
                                <span aria-hidden="true" style={{ marginRight: '0.35rem', fontSize: '0.85rem', lineHeight: 1 }}>
                                  {getAlertIcon(top.type)}
                                </span>
                                <span>{top.message}</span>
                              </span>
                              {alerts.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => toggleReasons(identity.id)}
                                  className="btn btn-link p-0 align-self-start"
                                  style={{ fontSize: '0.72rem', textDecoration: 'none' }}
                                >
                                  {expanded ? <ChevronDown size={10} className="me-1" /> : <ChevronRight size={10} className="me-1" />}
                                  {expanded ? 'Hide' : `Show all ${alerts.length} reasons`}
                                </button>
                              )}
                              {expanded && (
                                <div className="mt-1 d-flex flex-column gap-1">
                                  {sorted.map((alert, index) => (
                                    <div
                                      key={index}
                                      className={
                                        alert.type === 'critical' ? 'text-danger'
                                        : alert.type === 'warning' ? 'text-warning'
                                        : 'text-muted'
                                      }
                                      style={{ fontSize: '0.72rem', lineHeight: 1.3 }}
                                    >
                                      <strong>[{alert.type}]</strong> {alert.message}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                        {toLocaleStringInTimezone(identity.lastChecked, 'en-US', {}, tenantTimezone)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Alerts Section */}
      <Row className="mt-3">
        <Col>
          <Card>
            <Card.Header className="py-2">
              <span className="fw-bold" style={{ fontSize: '0.85rem' }}>Active Alerts</span>
            </Card.Header>
            <Card.Body>
              {identities.filter(i => i.alerts.length > 0).length === 0 ? (
                <div className="text-center py-3">
                  <CheckCircle size={36} className="text-success mb-2" />
                  <div className="fw-bold" style={{ fontSize: '0.85rem' }}>No Active Alerts</div>
                  <p className="text-muted mb-0" style={{ fontSize: '0.8rem' }}>All identities are performing well</p>
                </div>
              ) : (
                <div className="d-flex flex-column gap-2">
                  {identities
                    .filter(i => i.alerts.length > 0)
                    .map(identity =>
                      identity.alerts.map((alert, index) => {
                        const pillStyle = getAlertPillStyle(alert.type);
                        return (
                          <div
                            key={`${identity.id}-${index}`}
                            className="d-flex align-items-start"
                            style={{
                              ...pillStyle,
                              fontSize: '0.82rem',
                              padding: '0.5rem 0.75rem',
                              borderRadius: '0.375rem',
                              borderStyle: 'solid',
                              borderWidth: '1px',
                              borderLeftWidth: '3px',
                              lineHeight: 1.4,
                            }}
                          >
                            <span aria-hidden="true" style={{ marginRight: '0.5rem', fontSize: '1rem', lineHeight: 1.2 }}>
                              {getAlertIcon(alert.type)}
                            </span>
                            <div>
                              <strong>{identity.name}</strong> — {alert.message}
                            </div>
                          </div>
                        );
                      })
                    )
                  }
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
    </>
  );
};

export default IdentityHealthMonitoring;
